import { authenticatedFetch } from "./apiClient";
import {
  GoogleSearchConsoleAccountList,
  GoogleSearchConsoleCallbackResult,
  GoogleSearchConsoleConnectionResponse,
  GoogleSearchConsoleSitesResponse,
  GoogleSearchConsoleStatusResponse,
  SearchAnalyticsDataRequest,
  SearchAnalyticsDataResponse,
} from "@/types/GoogleSearchConsole";

const API_BASE_URL = import.meta.env.VITE_API_URL;
const GSC_CLOUD_FUNC_BASE_PATH = "/fn/integrations/google-search-console"; // goes to cloud function for a faster startup time
const GSC_CLOUD_RUN_BASE_PATH = "/v1/integrations/google-search-console"; // goes to core backend gcr worker

// The domain-scoped endpoints raise FastAPI HTTPExceptions with a plain-string
// `detail` (403 for non-members, 400 for a missing/disabled integration --
// see google_search_console.py's `except ValueError` / `except Exception`
// handlers). Surface that detail directly instead of the raw response body,
// which is a JSON blob and reads as noise, not a message.
async function readErrorDetail(response: Response): Promise<string> {
  try {
    const errorData = await response.json();
    if (typeof errorData?.detail === "string") return errorData.detail;
  } catch {
    // Response body wasn't JSON -- fall through to the generic message below.
  }
  return `request failed (${response.status})`;
}

export class GoogleSearchConsoleService {
  static async connect(
    redirectUrl?: string,
  ): Promise<GoogleSearchConsoleConnectionResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${GSC_CLOUD_FUNC_BASE_PATH}/connect`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          redirect_url: redirectUrl,
        }),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to initiate GSC connection: ${errorText}`);
    }

    return response.json();
  }

  static async getAllAccounts(): Promise<GoogleSearchConsoleAccountList> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${GSC_CLOUD_RUN_BASE_PATH}/accounts`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch GSC accounts: ${errorText}`);
    }

    return response.json();
  }

  static async checkStatus(
    accountId: string,
  ): Promise<GoogleSearchConsoleStatusResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${GSC_CLOUD_RUN_BASE_PATH}/status?account_id=${accountId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to check GSC status: ${errorText}`);
    }

    return response.json();
  }

  static async getSites(
    accountId: string,
  ): Promise<GoogleSearchConsoleSitesResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${GSC_CLOUD_RUN_BASE_PATH}/sites?account_id=${accountId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch GSC sites: ${errorText}`);
    }

    return response.json();
  }

  // `domainId` is required: the backend resolves the connected account, site
  // URL, and OAuth token owner from the domain's GSC integration doc and
  // authorizes the caller by org membership on that domain. Callers must
  // guard on having a current domain before invoking this -- there is no
  // sensible fallback for a missing domain id.
  static async runSearchAnalytics(
    domainId: string,
    dataRequest: SearchAnalyticsDataRequest,
  ): Promise<SearchAnalyticsDataResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${GSC_CLOUD_RUN_BASE_PATH}/search-analytics?domain_id=${encodeURIComponent(domainId)}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataRequest),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch GSC search analytics: ${await readErrorDetail(response)}`,
      );
    }

    return response.json();
  }

  static async disconnect(
    accountId: string,
  ): Promise<{ message: string; success: boolean }> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${GSC_CLOUD_RUN_BASE_PATH}/disconnect?account_id=${accountId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to disconnect GSC: ${errorText}`);
    }

    return response.json();
  }

  // Parse the callback query params the connectors function appends on return:
  // ?gsc_connected=true&account_id=... (success) or ?error=... (failure)
  static parseCallbackParams(url: string): GoogleSearchConsoleCallbackResult {
    const urlObj = new URL(url);
    const connected = urlObj.searchParams.get("gsc_connected") === "true";
    const accountId = urlObj.searchParams.get("account_id") || undefined;
    const error = urlObj.searchParams.get("error") || undefined;

    return { connected, accountId, error };
  }
}
